import { useRoomContext } from "@livekit/components-react";
import { captureException } from "@sentry/react";
import { Smile } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useUser } from "@/hooks/use-user";
import { SENTRY_ENABLED } from "@/lib/sentry";

type BroadcastReactionsProps = {
	onPointerDown?: (e: React.PointerEvent) => void;
	container?: HTMLElement | null;
};

type Reaction = {
	id: string;
	emoji: string;
	participantName: string;
};

type DataMessage = {
	type: "broadcast-reaction";
	emoji: string;
	participantName: string;
	timestamp: number;
};

const REACTIONS = [
	{ emoji: "👍", label: "Thumbs up" },
	{ emoji: "👏", label: "Clap" },
	{ emoji: "❤️", label: "Heart" },
	{ emoji: "😂", label: "Laugh" },
	{ emoji: "🎉", label: "Celebrate" },
	{ emoji: "🤔", label: "Thinking" },
];

const REACTION_DURATION_MS = 3000;
const MAX_VISIBLE_REACTIONS = 10;

function createReaction(data: DataMessage): Reaction {
	return {
		id: `${data.timestamp}-${Math.random()}`,
		emoji: data.emoji,
		participantName: data.participantName,
	};
}

export function useBroadcastReactions() {
	const room = useRoomContext();
	const [reactions, setReactions] = useState<Reaction[]>([]);

	useEffect(() => {
		if (!room) return;

		const addReaction = (reaction: Reaction) => {
			setReactions((prev) => [...prev, reaction]);
			setTimeout(() => {
				setReactions((prev) => prev.filter((r) => r.id !== reaction.id));
			}, REACTION_DURATION_MS);
		};

		const handleDataReceived = (payload: Uint8Array) => {
			try {
				const decoder = new TextDecoder();
				const data = JSON.parse(decoder.decode(payload));

				console.log("[BroadcastReaction] Received data:", data);

				if (data.type === "broadcast-reaction") {
					console.log("[BroadcastReaction] Adding reaction:", data);
					addReaction(createReaction(data as DataMessage));
				}
			} catch (error) {
				console.error("Failed to parse broadcast reaction:", error);
			}
		};

		const handleLocalReaction = (data: DataMessage) => {
			addReaction(createReaction(data));
		};

		room.on("dataReceived", handleDataReceived);
		(
			room as unknown as {
				on: (event: string, handler: (data: DataMessage) => void) => void;
			}
		).on("localBroadcastReaction", handleLocalReaction);

		return () => {
			room.off("dataReceived", handleDataReceived);
			(
				room as unknown as {
					off: (event: string, handler: (data: DataMessage) => void) => void;
				}
			).off("localBroadcastReaction", handleLocalReaction);
		};
	}, [room]);

	return { reactions };
}

export function BroadcastReactionOverlay() {
	const { reactions } = useBroadcastReactions();

	if (reactions.length === 0) return null;

	return (
		<div className="pointer-events-none absolute bottom-4 left-4 z-50 flex flex-col-reverse gap-1">
			{reactions.slice(-MAX_VISIBLE_REACTIONS).map((reaction) => (
				<div
					className="fade-in slide-in-from-left-2 flex animate-in items-center gap-1.5 rounded-full bg-black/60 px-2 py-1 backdrop-blur-sm"
					key={reaction.id}
					style={{
						animation: "broadcast-reaction-pop 0.2s ease-out",
					}}
				>
					<span className="text-base">{reaction.emoji}</span>
					<span className="max-w-20 truncate text-white text-xs">
						{reaction.participantName}
					</span>
				</div>
			))}
		</div>
	);
}

export function BroadcastReactions({
	onPointerDown,
	container,
}: BroadcastReactionsProps) {
	const room = useRoomContext();
	const { getUserDisplayName } = useUser();
	const [open, setOpen] = useState(false);

	const sendReaction = async (emoji: string) => {
		if (!room?.localParticipant) return;
		try {
			const encoder = new TextEncoder();
			const participantId =
				room.localParticipant.identity ||
				room.localParticipant.name ||
				"anonymous";
			const displayName = getUserDisplayName(participantId);
			const payload = {
				type: "broadcast-reaction",
				emoji,
				participantName: displayName,
				timestamp: Date.now(),
			};
			console.log("[BroadcastReaction] Sending reaction:", payload);
			const data = encoder.encode(JSON.stringify(payload));
			await room.localParticipant.publishData(data, { reliable: true });
			(
				room as unknown as { emit: (event: string, data: DataMessage) => void }
			).emit("localBroadcastReaction", {
				type: "broadcast-reaction",
				emoji,
				participantName: displayName,
				timestamp: Date.now(),
			});
			setOpen(false);
		} catch (error) {
			if (SENTRY_ENABLED)
				captureException(error, {
					tags: { context: "broadcast_reaction" },
				});
			toast.error("Failed to send reaction");
		}
	};

	return (
		<Popover onOpenChange={setOpen} open={open}>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<PopoverTrigger asChild>
							<Button
								onPointerDown={onPointerDown}
								size="icon"
								variant="secondary"
							>
								<Smile className="size-4" />
							</Button>
						</PopoverTrigger>
					</TooltipTrigger>
					<TooltipContent>Send Reaction</TooltipContent>
				</Tooltip>
			</TooltipProvider>
			<PopoverContent
				align="end"
				className="w-auto p-1.5"
				container={container}
				onPointerDown={onPointerDown}
			>
				<div className="flex gap-0.5">
					{REACTIONS.map((reaction) => (
						<Button
							className="size-8 p-0 text-lg transition-transform hover:scale-110"
							key={reaction.emoji}
							onClick={() => sendReaction(reaction.emoji)}
							title={reaction.label}
							variant="ghost"
						>
							{reaction.emoji}
						</Button>
					))}
				</div>
			</PopoverContent>
		</Popover>
	);
}
